import {addClass, createElement, createTemplate, setText} from "@acng/frontend-bounty";
import {joinArray, sizeOfArray, sliceOfArray} from "@acng/frontend-bounty/array.js";
import {IS, NULLABLE, typeguard} from "@acng/frontend-bounty/typeguard.js";
import {getElementById} from "@acng/frontend-bounty/dom/query.js";

import {compile} from "./compile.js";
import {insert} from "./insert.js";
import {createWeakMap, get, has, set} from "@acng/frontend-bounty/collection.js";

const MODULE = "stargazer/lookup";

/**
 * @todo The return behaviour is required for legacy config setLoader
 * @param {HTMLTemplateElement} template
 * @param {import("../model/resource.js").ResourceLoader} loader
 */
export function defineResourceLoader(template, loader) {
  ASSERT: if (has(loaders, template)) console.warn(MODULE, "replace loader", {template, loader});

  set(loaders, template, loader);
}

/**
 * @internal
 * @param {HTMLTemplateElement} template
 */
export const hasResourceLoader = (template) => has(loaders, template);

/**
 * @param {HTMLTemplateElement} namespace
 * @param {string[]} path
 * @returns {Promise<HTMLTemplateElement>}
 */
export const lookup = async (namespace, path) => {
  const resource = find(namespace, path) ?? (await fetch(namespace, path));

  await compile(resource);

  return resource;
};

const hiddenTemplate = createTemplate();
hiddenTemplate.hidden = true;

/**
 * @type {WeakMap<HTMLTemplateElement, import("../model/resource.js").ResourceLoader>}
 */
const loaders = createWeakMap();

/**
 * @todo
 * - This is a Array.reduce?
 *
 * @param {HTMLTemplateElement} resource
 * @param {string[]} path
 *
 * @returns {?HTMLTemplateElement}
 */
const find = (resource, path) => {
  let current = resource;

  if (current.hidden) {
    return hiddenTemplate;
  }

  for (const id of path) {

    const element = getElementById(current.content, id);

    ASSERT: typeguard(MODULE, element, NULLABLE(IS(HTMLTemplateElement)));

    if (!element) {
      return null;
    }

    if (element.hidden) {
      return hiddenTemplate;
    }

    current = element;
  }

  return current;
};

/**
 * @todo TODO Since the insert is synchronous loader could return a promise now which resolves to item or null (caching in core must be changed for that)
 *
 * @param {HTMLTemplateElement} namespace
 * @param {string[]} path
 * @returns {Promise<HTMLTemplateElement>}
 */
const fetch = async (namespace, path) => {
  let closest = namespace;
  let closestWithLoader = has(loaders, namespace) ? namespace : null;
  let nextPath = path;
  let nextIndex = 0;
  const l = sizeOfArray(path);

  while (true) {
    for (let i = nextIndex; i < l; i++) {
      const next = getElementById(closest.content, path[i]);
      ASSERT: typeguard(MODULE, next, NULLABLE(IS(HTMLTemplateElement)));

      if (!next) break;
      if (next.hidden) return hiddenTemplate;

      nextIndex = i + 1;
      closest = next;

      if (has(loaders, closest)) {
        closestWithLoader = closest;
        nextPath = sliceOfArray(path, nextIndex);
      }
    }

    if (nextIndex == l) {
      return closest;
    }

    if (!closestWithLoader) {
      DEBUG: console.warn(MODULE, "resource not found", {namespace, path});

      const element = createElement("strong");
      addClass(element, "drf-notfound");
      setText(element, `🚧 ${joinArray(path, "/")} 🚧`);
      return createTemplate("", element);
    }

    const loaderResource = closestWithLoader;
    try {
      await get(loaders, closestWithLoader)?.(nextPath, (item) => insert(loaderResource, item, nextPath));
    } catch (reason) {
      const element = createElement("strong");
      addClass(element, "drf-notfound");
      setText(element, `🚧${ reason } 🚧`);
      return createTemplate("", element);
    }
    closestWithLoader = null;
  }
};
